import { SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useAuth,
  getAnnotationToolSupervisorProjectDetailsPath,
  getAnnotationToolSupervisorProjectMetricsPath,
} from '@verantos-dev/core-annotation-tool';
import { DEFAULT_METRICS_VIEW } from '@verantos-dev/feature-annotation-metrics';
import { useGetSupervisorProjectsQuery } from '@verantos-dev/feature-supervisor-queue';

import { useGetSupervisorProjectByIdQuery } from './api/supervisorProjectsApiSlice';
import { SUPERVISOR_PROJECT_TABS, type SupervisorProjectTabType } from './constants';

export const useSupervisorProjectDetails = () => {
  const { projectId, metricsView } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetSupervisorProjectByIdQuery(
    { projectId: projectId as string },
    { skip: !projectId || projectId === '' }
  );

  // AC-1: role ANNOTATION_USER and assigned as Supervisor for this specific project, not
  // "supervises something somewhere".
  const shouldCheckSupervisorScope = user.isUser;
  const {
    data: supervisorProjectsData,
    isLoading: isSupervisorProjectsLoading,
    isFetching: isSupervisorProjectsFetching,
  } = useGetSupervisorProjectsQuery(undefined, { skip: !shouldCheckSupervisorScope });

  // Render nothing (not a disabled tab) while this is true, so a non-supervisor sees the
  // Overview page unchanged.
  const isMetricsAccessLoading =
    shouldCheckSupervisorScope && (isSupervisorProjectsLoading || isSupervisorProjectsFetching);
  const showMetricsTab =
    shouldCheckSupervisorScope &&
    !isMetricsAccessLoading &&
    Boolean(supervisorProjectsData?.data?.some((project) => project.id === projectId));

  // Remembers the last-viewed metrics view so switching Overview -> Metrics lands back where
  // the user left off within the session; the URL alone can't carry this across the round trip
  // since Overview has no metrics segment.
  const [lastMetricsView, setLastMetricsView] = useState<string>(
    () => metricsView ?? DEFAULT_METRICS_VIEW
  );
  useEffect(() => {
    if (metricsView) {
      setLastMetricsView(metricsView);
    }
  }, [metricsView]);

  const selectedTab: SupervisorProjectTabType = metricsView
    ? SUPERVISOR_PROJECT_TABS.METRICS
    : SUPERVISOR_PROJECT_TABS.OVERVIEW;

  const handleTabChange = (_event: SyntheticEvent, value: SupervisorProjectTabType) => {
    if (!projectId) {
      return;
    }
    if (value === SUPERVISOR_PROJECT_TABS.METRICS) {
      navigate(getAnnotationToolSupervisorProjectMetricsPath(projectId, lastMetricsView));
    } else {
      navigate(getAnnotationToolSupervisorProjectDetailsPath(projectId));
    }
  };

  return {
    projectId,
    projectName: projectDetailsData?.data?.basics?.projectName ?? '',
    createdOn: projectDetailsData?.data?.createdAt ?? '',
    startedOn: projectDetailsData?.data?.startDate ?? '',
    completedOn: projectDetailsData?.data?.completedDate ?? '',
    isProjectDetailsLoading: isProjectDetailsLoading || isProjectDetailsFetching,
    showMetricsTab,
    isMetricsAccessLoading,
    selectedTab,
    handleTabChange,
  };
};
